import { useEffect, useCallback, useMemo, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { getOutgoingCalls } from '../../../store/reducers/OutgoingCallsSlice';
import { getClinics } from '../../../store/reducers/ClinicSlice';
import { getSpecializationDoctor } from '../../../store/reducers/SpecializationDoctorSlice';
import { getSurnameDoctor } from '../../../store/reducers/surnameDoctor';
import {
  getReport,
  selectReportLoading,
} from '../../../store/reducers/ReportSlice';
import { useDebounce } from '../../shared/useDebounce';
import { useOutgoingCallsColumns } from './useOutgoingCallsColumns/useOutgoingCallsColumns';
import { useOutgoingCallsHandlers } from './useOutgoingCallsHandlers/useOutgoingCallsHandlers';
import { useOutgoingCallsState } from './useOutgoingCallsState/useOutgoingCallsState';
import { useFilterOption } from '../../shared/useFilterOption';
import { useJournalCampaignFilter } from '../../shared/useJournalCampaignFilter';
import {
  JournalFilters,
  useJournalQueryState,
} from '../../shared/useJournalQueryState';

const toDate = (value: string) => new Date(`${value}T00:00:00`);
const toSortDirection = (value: string): JournalFilters['sortDirection'] =>
  value === 'Desc' ? 'desc' : 'asc';

export const useOutgoingCalls = () => {
  const isReportLoading = useAppSelector(selectReportLoading);

  const {
    clinics,
    specializations,
    surnameDoctors,
    totalCount,
    status,
    setIndex,
    pageSize,
    setPageSize,
    callRequest,
    setCallRequest,
    callRequestReport,
    setCallRequestReport,
    total,
    currentOutgoingCalls,
    allCalls,
  } = useOutgoingCallsState();

  const dispatch = useAppDispatch();

  const scenarioFilter =
    callRequest['Filter.Types'] ?? callRequest['Filter.NotificationTypes'];
  const journalFilters = useMemo<JournalFilters>(
    () => ({
      dateRange: [
        toDate(callRequest['Filter.From']),
        toDate(callRequest['Filter.To']),
      ],
      textSearch: callRequest['Filter.TextSearch'],
      status: callRequest['Filter.States'],
      scenario: scenarioFilter,
      campaign: [
        ...(callRequest['Filter.FormBatchIds'] ?? []),
        ...(callRequest['Filter.DispensarizationBatchIds'] ?? []),
      ],
      medicalOrganization: callRequest['Filter.ClinicIds'],
      result: callRequest['Filter.Result'],
      attemptStatus: callRequest['Filter.AttemptStates'],
      attemptTimeRange:
        callRequest['Filter.AttemptFrom'] && callRequest['Filter.AttemptTo']
          ? [callRequest['Filter.AttemptFrom'], callRequest['Filter.AttemptTo']]
          : [],
      specialization: callRequest['Filter.SpecializationIds'],
      doctor: callRequest['Filter.DoctorIds'],
      pageIndex: callRequest.PageIndex,
      pageSize: callRequest.PageSize,
      sortField: callRequest.SortKeySelector,
      sortDirection: toSortDirection(callRequest.SortOrder),
    }),
    [callRequest, scenarioFilter],
  );

  const {
    activeFilterCount,
    isLoading: dictionariesLoading,
    dictionaries,
    scenarioLabelByType,
    resultLabelByValue,
  } = useJournalQueryState(journalFilters, 'outbound');

  useEffect(() => {
    dispatch(getClinics());
    dispatch(getSpecializationDoctor());
    dispatch(getSurnameDoctor());
  }, []);

  const debouncedTextSearch = useDebounce(
    callRequest['Filter.TextSearch'],
    500,
  );

  const debouncedClinic = useDebounce(callRequest['Filter.ClinicIds'], 500);

  const debouncedDoctor = useDebounce(callRequest['Filter.DoctorIds'], 500);
  const debouncedSpecialization = useDebounce(
    callRequest['Filter.SpecializationIds'],
    500,
  );
  const debouncedStates = useDebounce(callRequest['Filter.States'], 500);
  const debouncedAttemptStates = useDebounce(
    callRequest['Filter.AttemptStates'],
    500,
  );
  const debouncedResult = useDebounce(callRequest['Filter.Result'], 500);
  const debouncedScenarioTypes = useDebounce(scenarioFilter, 500);

  const dateRange = useMemo<[Dayjs, Dayjs]>(
    () => [dayjs(callRequest['Filter.From']), dayjs(callRequest['Filter.To'])],
    [callRequest['Filter.From'], callRequest['Filter.To']],
  );

  const prevTextSearchRef = useRef<string>('');

  useEffect(() => {
    const isTextSearch =
      prevTextSearchRef.current?.length !== debouncedTextSearch.length;
    const isTextSearchDelete =
      prevTextSearchRef.current?.length > debouncedTextSearch.length;

    prevTextSearchRef.current = debouncedTextSearch;
    if (isTextSearch) {
      if (debouncedTextSearch || isTextSearchDelete) {
        if (isTextSearchDelete) {
          dispatch(getOutgoingCalls(callRequest));
        } else if (
          debouncedTextSearch.length > 2 ||
          debouncedTextSearch.length == 0
        ) {
          dispatch(getOutgoingCalls(callRequest));
        }
      }
    } else if (
      debouncedClinic ||
      debouncedDoctor ||
      debouncedSpecialization ||
      debouncedStates ||
      debouncedAttemptStates ||
      debouncedResult ||
      debouncedScenarioTypes
    ) {
      dispatch(getOutgoingCalls(callRequest));
    } else {
      dispatch(getOutgoingCalls(callRequest));
    }
  }, [
    callRequest['Filter.From'],
    callRequest['Filter.To'],
    callRequest['Filter.AttemptFrom'],
    callRequest['Filter.AttemptTo'],
    callRequest['Filter.FormTemplateIds'],
    callRequest['Filter.FormBatchIds'],
    callRequest['Filter.DispensarizationBatchIds'],
    callRequest.PageIndex,
    callRequest.PageSize,
    callRequest.SortKeySelector,
    callRequest.SortOrder,
    debouncedTextSearch,
    debouncedClinic,
    debouncedDoctor,
    debouncedSpecialization,
    debouncedStates,
    debouncedAttemptStates,
    debouncedResult,
    debouncedScenarioTypes,
  ]);

  useEffect(() => {
    if (Math.floor(total / callRequest.PageSize) < callRequest.PageIndex) {
      setCallRequest((prev) => ({
        ...prev,
        PageIndex: Math.floor(total / callRequest.PageSize),
      }));
    }
  }, [total]);

  const { COLUMNS, hoveredStatus, onTableChange } = useOutgoingCallsColumns({
    onCallRequest: setCallRequest,
    callRequest,
    total,
    scenarioLabelByType,
    resultLabelByValue,
  });

  const divider = {
    height: 1,
    backgroundColor: '#daccccff',
    margin: '0',
  };

  const {
    handleSearch,
    handleRangePicker,
    handleChange,
    handlePageSizeChange,
    clinicValue,
    handleClinicsChange,
    resultValue,
    handleResultChange,
    taskStatusValue,
    handleTaskStatusChange,
    attemptStatusValue,
    handleAttemptStatusChange,
    specializationValue,
    handleSpecializationChange,
    doctorsValue,
    handleDoctorsChange,
    handleOpenClinics,
    handleOpenResult,
    handleOpenTaskStatus,
    handleOpenAttemptStatus,
    handleOpenSpecialization,
    handleOpenDoctors,
    notificationTypeValue,
    handleNotificationTypeChange,
    handleOpenNotificationType,
    attemptTimeRangeValue,
    handleAttemptTimeRangeChange,
    resetFilters,
    isResetDisabled,
  } = useOutgoingCallsHandlers({
    onIndex: setIndex,
    onCallRequest: setCallRequest,
    callRequest,
    onCallRequestReport: setCallRequestReport,
    callRequestReport,
    onPageSize: setPageSize,
    total,
  });

  const handleOkReport = useCallback(() => {
    dispatch(getReport(callRequestReport));
  }, [dispatch, callRequestReport]);

  const filterOption = useFilterOption();
  const campaignFilter = useJournalCampaignFilter({
    types: scenarioFilter,
    callRequest,
    onCallRequest: setCallRequest,
    callRequestReport,
    onCallRequestReport: setCallRequestReport,
  });
  const isTableLoading = status.responseLoading;
  const isFilterLoading = isTableLoading || dictionariesLoading;

  return {
    campaignFilter,
    setCallRequest,
    callRequest,
    dateRange,
    clinics,
    pageSize,
    isTableLoading,
    hasLoadError: status.responseError !== null,
    isFilterLoading,
    totalCount,
    total,
    handleChange,
    currentOutgoingCalls,
    specializations,
    surnameDoctors,
    handleRangePicker,
    handleSearch,
    allCalls,
    COLUMNS,
    onTableChange,
    divider,
    handlePageSizeChange,
    handleOkReport,
    filterOption,
    hoveredStatus,
    clinicValue,
    handleClinicsChange,
    resultValue,
    handleResultChange,
    taskStatusValue,
    handleTaskStatusChange,
    attemptStatusValue,
    handleAttemptStatusChange,
    specializationValue,
    handleSpecializationChange,
    doctorsValue,
    handleDoctorsChange,
    handleOpenClinics,
    handleOpenResult,
    handleOpenTaskStatus,
    handleOpenAttemptStatus,
    handleOpenSpecialization,
    handleOpenDoctors,
    notificationTypeValue,
    handleNotificationTypeChange,
    handleOpenNotificationType,
    attemptTimeRangeValue,
    handleAttemptTimeRangeChange,
    resetFilters,
    isResetDisabled,
    activeFilterCount,
    dictionariesLoading,
    taskStatusOptions: dictionaries.statuses,
    resultOptions: dictionaries.results,
    scenarioOptions: dictionaries.scenarios,
    scenarioLabelByType,
    resultLabelByValue,
    isReportLoading,
  };
};
